<script setup lang="ts">
/**
 * One card of a game window: a painted steel plate (`.k-card`) with a gold heading
 * (painted icon + game lettering) and an optional chip on the right. Owns the
 * card chrome only; each section (a Record block, a Settings group) fills the
 * default slot.
 */
import GameIcon from '../kit/GameIcon.vue';
import type { GameIconName } from '../kit/icons';

defineProps<{ title: string; icon?: GameIconName }>();
</script>

<template>
  <section class="k-card card">
    <header class="head">
      <GameIcon v-if="icon" :name="icon" :size="32" />
      <h3 class="k-title gold title">{{ title }}</h3>
      <div v-if="$slots.chip" class="chip-slot"><slot name="chip" /></div>
    </header>
    <slot />
  </section>
</template>

<style scoped>
.card {
  display: grid;
  /* One column that may shrink below its widest row: an implicit `auto` track
     grows to the min-content of a pack ladder or mode strip and pushes the
     window past a 390 px phone. */
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  align-content: start;
  min-width: 0;
}

.head {
  display: flex;
  /* On a 320 px phone the chips drop under the title instead of past the frame. */
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-height: 34px;
}

.title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 1.05rem;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip-slot {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
